/**
 * AI Chat Store & Shared Types
 *
 * Zustand store for conversation state, plus shared types
 * and constants used across all AI chat hooks.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AIChatCard, ChatExecutionMeta } from '../types/ai-chat.types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ConversationSummary {
  id: string;
  title: string;
  updatedAt: Date;
  messageCount: number;
  folder?: string | null;
  isArchived?: boolean;
}

export interface ConversationsListResponse {
  conversations: ConversationSummary[];
  total: number;
}

/**
 * Live "thinking" progress event emitted by the backend orchestrator while
 * it plans, runs tools, and decides on next steps (PDCA loop).
 */
export interface ThinkingEvent {
  stage: 'plan' | 'tool_call' | 'observe' | 'decide';
  message: string;
  toolName?: string;
  status?: 'info' | 'success' | 'error';
  detail?: Record<string, unknown>;
}

/**
 * Stream event types from the SSE backend
 */
export interface StreamEvent {
  type: 'start' | 'chunk' | 'thinking' | 'done' | 'error';
  content?: string;
  messageId?: string;
  userMessageId?: string;
  cards?: AIChatCard[];
  suggestions?: string[];
  conversationId?: string;
  executionMeta?: ChatExecutionMeta;
  thinking?: ThinkingEvent;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_HISTORY_LIMIT = 50;

/**
 * React Query cache keys for AI Chat
 */
export const AI_CHAT_QUERY_KEYS = {
  history: (conversationId?: string, limit: number = DEFAULT_HISTORY_LIMIT) =>
    ['ai-chat', 'history', conversationId, limit] as const,
  conversations: ['ai-chat', 'conversations'] as const,
  conversation: ['ai-chat', 'conversation'] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// Zustand Store
// ─────────────────────────────────────────────────────────────────────────────

interface ConversationStore {
  currentConversationId: string | null;
  setCurrentConversationId: (id: string | null) => void;
}

export const useConversationStore = create<ConversationStore>()(
  persist(
    (set) => ({
      currentConversationId: null,
      setCurrentConversationId: (id) => set({ currentConversationId: id }),
    }),
    {
      name: 'ai-chat-conversation',
    }
  )
);
